/**
 * 1_CypAdhdClinicalInterview.js — CYP ADHD Clinical Interview proforma.
 *
 * Renders the CI stage document's node tree
 * (assessmentStages/clinicalInterview.data — seeded from
 * python-functions/constants/cyp_adhd/ci_data_model.py and prefilled
 * server-side as informants complete their forms). The page is a GENERIC
 * renderer over that tree: it holds no question list of its own, so a form
 * question added to the registry appears here without a frontend change.
 *
 * Layout decisions:
 *   - One section at a time, with the section named in the URL
 *     (patient-information, mental-state-examination, risk-assessment, and
 *     so on) so a link opens that section. The page stays mounted.
 *   - Chrome matches the Adult Autism History page (Autism's name for the
 *     Clinical Interview). This is a precise port of
 *     6_clinicalInterview/autism/AdultAutismHistory.js:
 *       - the shared ModularSideNavbar (numbered badges, emerald tick once a
 *         section has been visited, brand-primary active row);
 *       - a fixed ModularStepFooter pinned under the scrollable content
 *         (Back named by destination | "n of N" | "Next: <section>"), with
 *         "Go to Semi-Structured Interview" as a phase skip link beneath the
 *         CTA on every section and as the forward action on the last one;
 *       - a 3xl unnumbered section title (the contract-type badge sits
 *         opposite it on Patient Information), gray-50 rounded-lg panels with
 *         a light shadow, w-56 line-item labels, icon column headers, text-xs
 *         answer boxes with an italic empty state, and the clinician notes box
 *         under a divider at the foot of each panel.
 *     Earlier this page copied the adult ADHD CI's chrome instead; the two
 *     child pathways now share one visual language with the autism modules.
 *   - The MSE and Risk Assessment steps have no autism History equivalent,
 *     so they take the same panel/table/button vocabulary as the autism
 *     HistoryRiskAssessmentPanel (which the Risk step renders directly).
 *   - Dual-column answers: nodes flagged hasGuardianColumn / hasSchoolColumn
 *     render a Parent/carer column and a School column side by side
 *     (Presenting Complaint and Education History); everything else is a
 *     single parent/carer column. Where a panel has a school column but a
 *     given question was never asked of the school, the slot is left EMPTY
 *     (History's InformantColumnPlaceholder) so the parent column keeps the
 *     same width on every row.
 *   - School contribution is ADVISORY: an empty school column renders a
 *     quiet "School input not received" state and never blocks anything.
 *   - Column headers are attributed from the guardianSource / schoolSource
 *     stamps written by the server-side prefill ("School (name, role)").
 *   - Section 1 (Patient Information) additionally shows the child's
 *     details from the patient header and who is participating. It has no
 *     clinician notes box: this stage never generates a paragraph, so notes
 *     typed here would not appear in the report. Adult ADHD and Adult Autism
 *     have the same dead-end box; hiding it is CYP-only for now.
 *   - The parent/carer panel on that section ends with an ID check. Ticking
 *     it records who confirmed the parent or carer's identity, and when.
 *     Moving forward while it is still unticked opens a reminder: the
 *     clinician can record the check there, or continue without it. Going
 *     back does not ask. The interview is not blocked.
 *   - Every other subsection has a clinician smart-notes box
 *     (StructuredNotesEditor with the CYP source tags — ASMT-29), persisted
 *     via the context's debounced updateAssessmentStage.
 */

import React, { useContext, useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../../../components/Layout';
import Breadcrumb from '../../../components/Breadcrumb';
import PreviewModeBanner from '../../../components/PreviewModeBanner';
import { useIsPreviewMode } from '../../../hooks/useIsPreviewMode';
import { usePreviewAwareNavigate } from '../../../hooks/usePreviewAwareNavigate';
import { ChevronRightIcon, UserIcon, UsersIcon } from '@heroicons/react/outline';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import ModularSideNavbar from '../../../components/autismModular/ModularSideNavbar';
import ModularStepFooter from '../../../components/autismModular/ModularStepFooter';
import PatientContractTypeBadge from '../../../components/PatientContractTypeBadge';
import { useDragAutoScroll } from '../../../hooks/useDragAutoScroll';
import { canStartAppointment } from '../../../utils/appointmentStageRouting';
import HistorySourceDataDialog from '../6_clinicalInterview/autism/HistorySourceDataDialog';
import { useAuth } from '../../../hooks/useAuth';
import { recordInferredAppointmentDateOnCompletion } from '../../../utils/appointmentDateHelpers';
import StructuredNotesEditor from '../12_observation/components/StructuredNotesEditor';
import HistoryRiskAssessmentPanel from '../6_clinicalInterview/autism/HistoryRiskAssessmentPanel';
import {
  normalizeHistoryRiskAssessmentData,
  hasAnyRiskAssessmentInput,
} from '../6_clinicalInterview/autism/historyRiskAssessmentConfig';
import CypAdhdMsePanel from './components/CypAdhdMsePanel';
import {
  CYP_CLINICAL_OBSERVATION_SUBSECTIONS,
  CYP_CLINICAL_OBSERVATION_INTRO,
  CYP_CLINICAL_OBSERVATION_TITLE,
  CYP_CLINICAL_OBSERVATION_DECISION_LABEL,
  CYP_CLINICAL_OBSERVATION_DECISION_TOOLTIP,
} from './config/cypAdhdClinicalObservationConfig';
import CypAdhdStructuredRiskPanel, {
  CYP_RISK_NOTICE_ID,
  hasAnyStructuredRiskInput,
  normalizeStructuredRiskData,
} from './components/CypAdhdStructuredRiskPanel';
import {
  isAcknowledgementCurrent,
  listYesRiskIndicators,
} from './config/cypAdhdStructuredRiskConfig';
import CypAdhdMedicalScreeningPanel from './components/CypAdhdMedicalScreeningPanel';
import {
  groupForSection as medicalScreeningGroupForSection,
  normalizeMedicalScreeningData,
} from './config/cypAdhdMedicalScreeningConfig';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import { CYP_NOTE_TAGGING_ENABLED, CYP_NOTE_TAG_OPTIONS } from '../../../constants/cypAdhdNoteTagOptions';
import { ModularPatientDataContext } from '../../../context/ModularPatientDataContext';
import { useCanEditStage } from '../../../hooks/useCanEditStage';
import { useModularSectionExitRegeneration } from '../../../hooks/useModularSectionExitRegeneration';
import { TriggerGenerateDocumentSection } from '../../../utils/TriggerGenerateDocumentSection';
import { normaliseNotesValue } from '../../../utils/autismReviewData';
import { moveNoteBetweenArrays } from '../../../utils/structuredNotesMove';
import { deepMerge } from '../../../utils/deepMerge';
import { resolvePatientFirstNameInText } from '../../../utils/resolveFormTemplates';
import { refreshDobAge } from '../../serviceUsers/3_forms/functions/PrefillFunctions';
import { getCypPatientSexAtBirth, cypPeriodsContentApplies } from '../../../utils/conditionChecks';

/**
 * Stable key of the Developmental History "Puberty" panel in the CI data
 * model (constants/cyp_adhd/ci_data_model.py). Matched on subsectionVarName,
 * not the display title, so a renamed heading cannot un-hide it.
 */
const PUBERTY_SUBSECTION_VAR_NAME = 'puberty';

/**
 * Subsections of a CI section that apply to this child, in display order.
 *
 * The only patient-dependent panel today is "Puberty": the parent is not
 * asked about periods when the child's sex assigned at birth is recorded as
 * Male or Intersex (see cypPeriodsContentApplies), so the clinician should not
 * see an empty panel asking for it either. Every other value, including no
 * value at all, keeps the panel: an unnecessary panel is a small annoyance,
 * a missing one is clinical content the clinician never sees.
 *
 * Used for BOTH the rendered panels and the note "Move to" picker so the two
 * agree, and panels are numbered after filtering so the visible numbering
 * stays contiguous.
 *
 * @param {Object} section - A CI section node ({ subsections, ... })
 * @param {boolean} showPeriodsContent - Result of cypPeriodsContentApplies for this patient
 * @returns {Array<[string, Object]>} [subKey, subsection] entries to show
 */
export const visibleCiSubsections = (section, showPeriodsContent) =>
  sortedEntries(section?.subsections || {}).filter(
    ([, subsection]) =>
      showPeriodsContent || subsection?.subsectionVarName !== PUBERTY_SUBSECTION_VAR_NAME,
  );

const STAGE_KEY = 'clinicalInterview';

// Sidebar ids for the clinician-completed steps. MSE renders the CYP MSE
// panel (structure and options unchanged from adult, per the ASMT-18
// decision), persisting to the ci stage doc's "mse" field; Risk renders the
// shared prop-driven HistoryRiskAssessmentPanel, persisting to
// "riskAssessment" — the same field name the autism history stage uses.
const MSE_SECTION_ID = 'mentalStateExamination';
const RISK_SECTION_ID = 'riskAssessment';

/** Address-bar form of a section title: "Patient Information" -> patient-information. */
function ciSectionSlug(title) {
  return String(title || '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Stable URL slug for one CI step. MSE and Risk keep the adult ADHD names
 * even when the on-screen heading differs. Data sections use their title.
 */
function slugForSectionKey(key, sections) {
  if (key === MSE_SECTION_ID) return 'mental-state-examination';
  if (key === RISK_SECTION_ID) return 'risk-assessment';
  const section = (sections || []).find(([sectionKey]) => sectionKey === key)?.[1];
  return ciSectionSlug(section?.sectionTitle) || `section-${key}`;
}

/** Sort a {'1': ..., '2': ...} map into [key, value] pairs by numeric key. */
function sortedEntries(map) {
  return Object.entries(map || {}).sort(([a], [b]) => Number(a) - Number(b));
}

/**
 * Panel heading as it appears on the page: "1.", "2.", "3." in the order the
 * panels are shown, not the stored "1.1" / "7.2" which repeats the stage
 * number. The stored title is left alone (report generation still reads it).
 */
function numberedPanelTitle(rawTitle, index) {
  const bare = String(rawTitle || '').replace(/^\d+(?:\.\d+)*\s+/, '').trim();
  return bare ? `${index + 1}. ${bare}` : `${index + 1}.`;
}

/** Column header for the school column, attributed when the source is known. */
function schoolColumnHeader(schoolSource) {
  if (!schoolSource?.name) return 'School';
  const role = schoolSource.relationship ? `, ${schoolSource.relationship}` : '';
  return `School (${schoolSource.name}${role})`;
}

/** Primary forward CTA — brand fill (Complete, or Go to SSI). Verbatim from
    AdultAutismHistory's HISTORY_PRIMARY_FORWARD_BTN so the two modules' final
    buttons are pixel-identical. */
const PRIMARY_FORWARD_BTN =
  'flex items-center py-2 px-4 rounded-md shadow-sm transition-all duration-200 text-white bg-primary hover:bg-primary/90';

/** Panel surface shared by every card on this page — the autism History's
    section panel (gray-50, rounded-lg, gray-300 border, light shadow). The MSE
    and structured-risk panels use the same string so the three step types
    read as one module. */
const CI_PANEL_CLASS =
  'bg-gray-50 rounded-lg border border-gray-300 text-left p-6 shadow-sm';

/** One read-only answer box — AdultAutismHistory's PrefillDisplayArea.

    text-xs on a white, gray-200-bordered box with a 40px floor so empty and
    answered rows sit at the same height; the empty state is italic grey. The
    WORDING of the empty state stays CYP's ("No answer given" / "School input
    not received") because it carries clinical meaning the autism placeholder
    ("Not yet provided") does not — only the styling is ported. */
function AnswerBox({ value, emptyText }) {
  const isEmpty = !value;
  return (
    <div className={`
      flex-1 min-w-0 min-h-[40px] px-3 py-2 text-xs border rounded-md whitespace-pre-wrap
      ${isEmpty
        ? 'text-gray-400 italic border-gray-200 bg-white'
        : 'text-gray-900 border-gray-200 bg-white'}
    `}>
      {isEmpty ? emptyText : value}
    </div>
  );
}

/** Line-item label — AdultAutismHistory's LineItemLabel (w-56, semibold sm). */
function LineItemLabel({ title }) {
  return (
    <div className="w-56 pr-4 flex-shrink-0 flex items-start gap-2">
      <span className="font-semibold text-sm text-gray-800 leading-snug">{title}</span>
    </div>
  );
}

/** Empty school slot — History's InformantColumnPlaceholder. Keeps the parent
    column the same width on every row of a dual panel even when the school
    was never asked this question. An empty slot claims nothing, whereas a
    "no answer" box would imply the school was asked and did not reply. */
function SchoolColumnPlaceholder() {
  return <div className="flex-1 min-w-0 basis-0" aria-hidden="true" />;
}

/** Column headers above the answers — History's SubsectionColumnHeaders.

    Centred text-xs brand-coloured labels with a person icon: one person for
    the parent/carer, the group icon for the school (as autism uses for its
    informant column). The school header carries the attribution stamp UNLESS
    plainLabels is set (used on Patient Information panels where the Name and
    Relationship rows already carry those facts — no adjacent duplication). */
function SubsectionColumnHeaders({ hasSchoolColumn, schoolSource, plainLabels }) {
  return (
    <div className="flex-1 flex gap-4">
      <div className="flex-1 flex items-center justify-center gap-1.5 text-xs text-primary">
        <UserIcon className="w-4 h-4" aria-hidden="true" />
        <span>Parent/carer</span>
      </div>
      {hasSchoolColumn && (
        <div className="flex-1 flex items-center justify-center gap-1.5 text-xs text-primary">
          <UsersIcon className="w-4 h-4" aria-hidden="true" />
          <span>{plainLabels ? 'School' : schoolColumnHeader(schoolSource)}</span>
        </div>
      )}
    </div>
  );
}

/** A single question node: label on the left, one or two answer columns.

    History's LineItemRow: a w-56 label, then the answer columns as equal
    flex-1 basis-0 cells (never a CSS grid) so a dual panel's two columns line
    up down the panel and a single-column panel's answer spans the rest. */
function QuestionRow({
  node, panelHasSchoolColumn, patientFirstName,
  guardianFormInstances, schoolFormInstances,
}) {
  const showGuardian = node.hasGuardianColumn !== false;
  const showSchool = Boolean(node.hasSchoolColumn);
  // CI node titles are copied from the service-user form definitions at patient
  // creation, so they still carry the {{patientFirstName}} personalisation
  // token — rendered raw, a clinician read "Medicines {{patientFirstName}}
  // takes now". The extractor already converts the token to [NAME] for the
  // prompt; this is the same substitution for the screen.
  const rowTitle = resolvePatientFirstNameInText(node.title, patientFirstName);

  // The child's date of birth is stored with the age the server calculated
  // when the parent/carer submitted the form. A child has a birthday between
  // referral and appointment often enough that the stored age is routinely
  // out of date, so it is recalculated for today here. Same helper as the
  // adult CI pages; every other row shows its stored value as written. The
  // refreshed value feeds both the answer box and the magnifier dialog so the
  // two never disagree about the child's age.
  const isDateOfBirthRow = node.providerVarName === 'dateOfBirth';
  const guardianValue = isDateOfBirthRow ? refreshDobAge(node.guardianPrefill) : node.guardianPrefill;
  const schoolValue = isDateOfBirthRow ? refreshDobAge(node.schoolPrefill) : node.schoolPrefill;

  // Magnifying-glass source dialog (HK testing, 2 Sept): the row label is the
  // short clinical category, so the magnifier shows the full question exactly
  // as each person was asked it — parent and school get materially different
  // wording under the same variable. One magnifier at the END of each answer
  // box, as on the adult CI (Eve, 3 Sept) — never beside the row title.
  const sourceVarNames = node.prefilledByInformantVarNames || [];
  const sourceDialog = (role) => {
    if (sourceVarNames.length === 0) return null;
    const isGuardian = role === 'guardian';
    const formInstances = isGuardian ? guardianFormInstances : schoolFormInstances;
    if (!formInstances) return null;
    const who = isGuardian ? 'parent/carer' : 'school representative';
    return (
      <HistorySourceDataDialog
        varNames={sourceVarNames}
        formInstances={formInstances}
        dialogTitle={isGuardian
          ? 'Parent/carer form responses'
          : 'School representative form responses'}
        lineItemTitle={rowTitle}
        displayPrefillText={(isGuardian ? guardianValue : schoolValue) || null}
        lastUpdatedAt={(isGuardian
          ? node.guardianPrefillLastUpdatedAt
          : node.schoolPrefillLastUpdatedAt) || null}
        patientFirstName={patientFirstName}
        triggerLabel={`View the ${who}'s source form responses for ${rowTitle}`}
      />
    );
  };

  // History's PrefillColumn: the answer box with its magnifier beside it.
  const answerColumn = (role, value, emptyText) => (
    <div className="flex-1 min-w-0 basis-0 flex items-start gap-1">
      <AnswerBox value={value} emptyText={emptyText} />
      {sourceDialog(role)}
    </div>
  );

  return (
    <div className="flex items-start mb-4">
      <LineItemLabel title={rowTitle} />
      <div className="flex-1 flex gap-4 items-stretch min-w-0">
        {showGuardian
          ? answerColumn('guardian', guardianValue, 'No answer given')
          : <SchoolColumnPlaceholder />}
        {panelHasSchoolColumn && (
          showSchool
            // Deliberately NOT the adult's "No answer given": school input is
            // advisory for CYP, and this wording says the school has not come
            // back rather than implying the answer is missing from the record.
            ? answerColumn('school', schoolValue, 'School input not received')
            : <SchoolColumnPlaceholder />
        )}
      </div>
    </div>
  );
}

/** Empty stand-in for the magnifying-glass button that ends every other
    answer in this panel.

    The child's name is taken from the patient record, not from a form the
    parent or school filled in, so there is no source response to open and
    no button to show. Every neighbouring box stops short of the column edge
    to leave room for that button. Without the same gap, the name box runs
    a few pixels wider and the column looks uneven (tester feedback, Oct
    2026). This slot copies the button's size exactly: the trigger in
    HistorySourceDataDialog is px-1 of padding around a 12px (h-3 w-3) icon,
    and the row already adds gap-1 between the box and whatever follows it.
    It is hidden from assistive technology because it does nothing. */
function MagnifierSlotSpacer() {
  return (
    <span
      className="flex-shrink-0 mt-2 flex items-center rounded-full px-1 py-1"
      aria-hidden="true"
    >
      <span className="block h-3 w-3" />
    </span>
  );
}

/**
 * Wording of the parent/carer ID check. First person, like the Risk
 * Assessment acknowledgement, because the clinician is signing their own
 * action rather than the system deciding the ID was valid.
 */
const PARENT_CARER_ID_LABEL = 'I have verified the ID of the parent/carer';

/**
 * The line shown under the ID checkbox once it has been ticked.
 *
 * Built from the saved record (name and time), not from whoever happens to
 * be looking at the page now, so a colleague opening the interview later
 * still sees who actually made the check. Returns null when there is
 * nothing to show: the box is unticked, or the saved time cannot be read.
 */
function formatParentCarerIdStamp(verification) {
  if (!verification?.verified || !verification.verifiedAt) return null;
  const when = new Date(verification.verifiedAt);
  if (Number.isNaN(when.getTime())) return null;
  const who = verification.verifiedByName ? ` by ${verification.verifiedByName}` : '';
  return `Verified${who} on ${when.toLocaleString('en-GB')}`;
}

/**
 * ID check at the foot of the parent/carer panel, and again inside the
 * forward-navigation reminder.
 *
 * Clinicians confirm they have seen identification for the parent or carer
 * attending with the child. Nothing here is sent to the report draft.
 * Unticking removes the saved record, so a mistaken tick does not stay on
 * the patient's file as if the check had been done.
 *
 * `divided` draws the rule that separates this from the answers on the
 * panel. The reminder dialog uses the same control without that rule.
 */
function ParentCarerIdVerification({
  verification, canEdit, onChange, divided = false, inputId,
}) {
  const stamp = formatParentCarerIdStamp(verification);
  return (
    <div className={divided ? 'mt-4 pt-4 border-t border-gray-200' : ''}>
      <label
        htmlFor={inputId}
        className={`flex items-center gap-3 min-h-[44px] ${
          canEdit ? 'cursor-pointer' : 'cursor-not-allowed'
        }`}
      >
        <input
          id={inputId}
          type="checkbox"
          checked={Boolean(verification?.verified)}
          disabled={!canEdit}
          onChange={(event) => onChange(event.target.checked)}
          className="h-5 w-5 flex-shrink-0 text-primary border-gray-300 rounded focus:ring-2 focus:ring-primary focus:ring-offset-2"
        />
        <span className="text-base font-medium text-gray-900">
          {PARENT_CARER_ID_LABEL}
        </span>
      </label>
      {stamp && (
        <p className="text-sm text-gray-600 mt-1">
          {stamp}
        </p>
      )}
    </div>
  );
}

/**
 * Reminder shown when a clinician moves forward in the Clinical Interview
 * while the parent/carer ID check is still unticked.
 *
 * Same shell as the CYP SSI "Clinical Decisions Required" dialog: the
 * clinician can fix the issue here (tick the box) or override ("Proceed
 * Anyway"). Cancel / Action button pair follows the HistoryRiskAssessment
 * and MseChangeConfirmDialog patterns.
 */
function ParentCarerIdCheckDialog({
  open,
  verification,
  canEdit,
  onVerificationChange,
  onStay,
  onContinue,
}) {
  const verified = Boolean(verification?.verified);
  return (
    <AlertDialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onStay();
      }}
    >
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50" />
        <AlertDialog.Content
          className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-6 w-[480px] max-w-[90vw] text-left shadow-xl"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            document.getElementById('parent-carer-id-check-dialog')?.focus();
          }}
        >
          <AlertDialog.Title className="text-lg font-bold mb-2">
            ID verification
          </AlertDialog.Title>
          <AlertDialog.Description className="text-sm text-gray-600 mb-5">
            The parent/carer ID check has not been completed. You can verify
            it here or proceed without it.
          </AlertDialog.Description>
          <ParentCarerIdVerification
            verification={verification}
            canEdit={canEdit}
            onChange={onVerificationChange}
            inputId="parent-carer-id-check-dialog"
          />
          <div className="flex justify-end gap-3 mt-6">
            <AlertDialog.Cancel asChild>
              <button
                type="button"
                className="py-2 px-4 font-semibold border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </AlertDialog.Cancel>
            <button
              type="button"
              onClick={onContinue}
              className={`py-2 px-4 font-semibold rounded-md transition-colors ${
                verified
                  ? 'bg-primary text-white hover:bg-primary/90'
                  : 'font-semibold text-gray-900 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              {verified ? 'Continue' : 'Proceed Anyway'}
            </button>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}

/** Static "Name" row prepended to the child details panel on Patient Information.
    The child's name comes from the patient header, not from a form question, so
    it has no CI node. Rendered with the same LineItemLabel + AnswerBox styling as
    a real prefill row, plus an empty slot where the magnifier would sit so the
    box is the same width as the rows below it. */
function StaticChildNameRow({ header }) {
  const childName = `${header?.firstName || ''} ${header?.lastName || ''}`.trim();
  return (
    <div className="flex items-start mb-4">
      <LineItemLabel title="Name" />
      <div className="flex-1 flex gap-4 items-stretch min-w-0">
        <div className="flex-1 min-w-0 basis-0 flex items-start gap-1">
          <AnswerBox value={childName || null} emptyText="Not recorded yet" />
          <MagnifierSlotSpacer />
        </div>
      </div>
    </div>
  );
}

export default function CypAdhdClinicalInterview() {
  const params = useParams();
  const patientId = params.patientId;
  // '' on the bare module URL; "patient-information", "risk-assessment" and
  // so on for a hotlink. The page stays mounted as the slug changes.
  const sectionSlug = String(params['*'] || '').split('/')[0];
  const navigate = usePreviewAwareNavigate();
  const isPreviewMode = useIsPreviewMode();
  const { user, claims } = useAuth();

  // The scrollable content column. Section changes scroll it back to the top,
  // and useDragAutoScroll scrolls it while a note chip is dragged near its
  // top or bottom edge so clinicians can reach off-screen notes boxes without
  // dropping and re-grabbing (AdultAutismHistory parity).
  const contentRef = useRef(null);
  useDragAutoScroll(contentRef);
  // Sections left during this visit. The stage document in context lags the
  // click (the write is debounced), so the "has every section been visited?"
  // check cannot rely on context alone — the same reason progress is counted
  // from a local set in goToSection. A reload still works: the stamps are
  // then on the stage document and this set starts empty.
  const visitedThisSession = useRef(new Set());
  // The section a forward move is waiting on, held in a ref as well as state
  // so confirming the reminder still knows the destination after the dialog
  // starts to close.
  const idCheckPromptIndexRef = useRef(null);

  const {
    header,
    assessmentStages,
    reportContent,
    informantFormInstances,
    updateAssessmentStage,
    flushStageWrites,
    error,
  } = useContext(ModularPatientDataContext);

  const canEdit = useCanEditStage(header || {}, 'CI', { previewMode: isPreviewMode });

  // Workflow config from the patient's stamp — chooses the risk panel shape
  // (CYP pages read only the wf.cyp* helpers).
  const wf = useWorkflowConfig(header, claims);

  // The snapshotted form instances for each informant role, so the magnifier
  // can show the full question wording exactly as that person was asked it.
  // Pure derivation — the context already holds every informant's forms.
  const informantForms = useMemo(() => {
    const result = { guardian: null, school: null };
    Object.entries(header?.informants || {}).forEach(([uid, entry]) => {
      const forms = informantFormInstances?.[uid];
      if (!forms || Object.keys(forms).length === 0) return;
      if (entry?.informantRole === 'legalGuardian' && !result.guardian) result.guardian = forms;
      if (entry?.informantRole === 'schoolRep' && !result.school) result.school = forms;
    });
    return result;
  }, [header?.informants, informantFormInstances]);

  const ciStage = assessmentStages?.[STAGE_KEY];
  const ciData = ciStage?.data || {};
  const sections = useMemo(() => sortedEntries(ciData.sections), [ciData.sections]);

  // Whether the "Puberty" panel applies to this child. Read from the patient
  // header, which carries the clinic's setup value overwritten by the
  // parent's confirmed answer. Fail-open: only an explicit Male or Intersex
  // hides it (see visibleCiSubsections).
  const showPeriodsContent = cypPeriodsContentApplies(getCypPatientSexAtBirth(header));

  const [activeSectionKey, setActiveSectionKey] = useState(() => {
    const fromSlug = (slug) => {
      if (slug === 'mental-state-examination') return MSE_SECTION_ID;
      if (slug === 'risk-assessment') return RISK_SECTION_ID;
      const match = sections.find(([, section]) => ciSectionSlug(section?.sectionTitle) === slug);
      return match ? match[0] : null;
    };
    return fromSlug(sectionSlug) || '1';
  });
  // Empty until this page writes a slug. A URL that arrives before the stage
  // document has loaded must wait, or a hotlink would be replaced by the
  // Mental State Examination fallback (the only steps known that early).
  const slugWeSet = useRef('');
  // Local optimistic notes state, keyed by dot path (same pattern as the
  // autism pages: components own local state; the context write is debounced).
  const [localNotes, setLocalNotes] = useState({});

  // Risk assessment: local optimistic copy of ci stage riskAssessment,
  // deep-merged on each panel update (mirrors AdultAutismHistory exactly).
  const [riskAssessmentData, setRiskAssessmentData] = useState(() => (
    normalizeHistoryRiskAssessmentData(ciStage?.riskAssessment)
  ));
  React.useEffect(() => {
    setRiskAssessmentData(normalizeHistoryRiskAssessmentData(ciStage?.riskAssessment));
  }, [ciStage?.riskAssessment]);

  // Structured risk assessment (TOC's NEST-parity panel): the org's stamped
  // workflow config chooses which risk panel this patient gets. Stored under
  // its own field (structuredRiskAssessment) so the two shapes never collide;
  // updates replace the object wholesale (detail selections are arrays, so
  // deep-merge would resurrect deselected options).
  const useStructuredRisk = wf.cypUseStructuredRiskAssessment;
  const [structuredRiskData, setStructuredRiskData] = useState(() => (
    normalizeStructuredRiskData(ciStage?.structuredRiskAssessment)
  ));
  React.useEffect(() => {
    setStructuredRiskData(normalizeStructuredRiskData(ciStage?.structuredRiskAssessment));
  }, [ciStage?.structuredRiskAssessment]);

  // Treatment contraindication screening (WLC, cypAdhdFlow.medicalScreening
  // === contraindicationsV1): clinician Yes/No + detail per NICE NG87 item,
  // shown as the last panel of the Medical History and Family History
  // sections. Stored under its own top-level field (medicalScreening) and
  // replaced wholesale on each change, the structured-risk contract. The
  // local copy exists so the toggles respond before the debounced write
  // lands and is re-synced whenever Firestore sends a new value.
  const showMedicalScreening = Boolean(wf.cypShowMedicalScreening);
  const [medicalScreeningData, setMedicalScreeningData] = useState(() => (
    normalizeMedicalScreeningData(ciStage?.medicalScreening)
  ));
  React.useEffect(() => {
    setMedicalScreeningData(normalizeMedicalScreeningData(ciStage?.medicalScreening));
  }, [ciStage?.medicalScreening]);

  // Parent/carer ID check on Patient Information. Kept as its own field on
  // the Clinical Interview stage document (next to mse and the risk
  // assessment), not inside the prefill question tree: a later form prefill
  // rewrites question answers by dot-path and must not wipe this record.
  // The local copy paints the tick before the debounced Firestore write
  // lands, then follows the stage document when a snapshot arrives (a
  // refresh, or the same interview open in another tab).
  const [parentCarerIdVerification, setParentCarerIdVerification] = useState(
    () => ciStage?.parentCarerIdVerification || null,
  );
  React.useEffect(() => {
    setParentCarerIdVerification(ciStage?.parentCarerIdVerification || null);
  }, [ciStage?.parentCarerIdVerification]);
  // Set while a forward move is paused on the ID reminder. Null means no
  // reminder is open. The number is an index into stepKeys.
  const [idCheckPromptIndex, setIdCheckPromptIndex] = useState(null);

  // A Yes risk indicator has to be acknowledged before this clinician can
  // leave for the Semi-Structured Interview. The checkbox is on the Risk
  // step; the same lock covers the "Go to Semi-Structured Interview" link
  // on earlier steps, so skipping ahead cannot dodge it. Preview and other
  // read-only viewers cannot tick the box (the panel disables it), so the
  // lock applies only while this viewer can edit — otherwise a preview of
  // the pathway would stop at Risk with no way through.
  const yesRiskIndicators = useStructuredRisk
    ? listYesRiskIndicators(structuredRiskData?.categories)
    : [];
  const riskNoticeUnacknowledged = Boolean(
    useStructuredRisk
    && canEdit
    && yesRiskIndicators.length > 0
    && !isAcknowledgementCurrent(structuredRiskData?.acknowledgement, yesRiskIndicators),
  );
  const riskAcknowledgementTitle = riskNoticeUnacknowledged
    ? 'Tick the acknowledgement under For Consideration before continuing to the Semi-Structured Interview'
    : undefined;
  // Shown on the greyed Risk-step button itself. Naming the checkbox and
  // saying that the button jumps to it, because a native disabled control
  // would neither explain itself on hover nor accept the click.
  const riskForwardHint = riskNoticeUnacknowledged
    ? 'Tick “I acknowledge this notice…” under For Consideration before continuing. Select this button to jump to that checkbox.'
    : undefined;

  // MSE: local optimistic copy of the ci stage's mse field. Merge semantics
  // are handled by mergeMseCategory/handleMseUpdate below; this declaration
  // lives here (above the generation hooks) so shouldRegenerate and
  // triggerCiGeneration can reference the live copy rather than the
  // debounced Firestore snapshot (which lags behind local edits by ~200ms).
  const [mseData, setMseData] = useState(() => ciStage?.mse || {});
  React.useEffect(() => {
    setMseData(ciStage?.mse || {});
  }, [ciStage?.mse]);

  // Step order: the prefill sections from the stage data, then the two
  // clinician-completed steps (MSE, then Risk Assessment — the adult order).
  //
  // noObservationInSsi (WLC and TOC) keeps this step and fills it with the
  // child-specific Clinical Observation questions. observationInSsi, the
  // other variant, drops the step entirely — not greyed out, not left empty
  // — because that clinician's first-hand account lives in the Clinical
  // Observation phase at the start of the SSI, and Risk follows the last
  // prefill section. No org stamps observationInSsi. The branch stays so
  // the org document can select it. The CI paragraph pipeline already skips
  // the MSE section when there is no data, so no empty paragraph is drafted.
  const hideMseStep = Boolean(wf.cypHideCiMse);
  const stepKeys = useMemo(
    () => [
      ...sections.map(([key]) => key),
      ...(hideMseStep ? [] : [MSE_SECTION_ID]),
      RISK_SECTION_ID,
    ],
    [sections, hideMseStep],
  );
  const activeIndex = Math.max(0, stepKeys.indexOf(activeSectionKey));
  const isMseStep = activeSectionKey === MSE_SECTION_ID;
  const isRiskStep = activeSectionKey === RISK_SECTION_ID;
  const [, activeSection] = sections[activeIndex] || [];

  // CISideNavbar wants [{ sectionTitle, status }]; MSE and Risk are steps
  // rather than data-model sections, so their visited stamps live under
  // data.visitedSteps (written by goToSection when the clinician moves on —
  // the green tick means "you have been here", the adult CI's semantics).
  const visitedSteps = ciData.visitedSteps || {};
  // Display title only: the step's report section stays "Mental State
  // Examination" (sectionTitleForKey below), which the backend keys on.
  const useClinicalObservationMse = wf.cypUseClinicalObservationMse;
  const mseStepTitle = useClinicalObservationMse
    ? CYP_CLINICAL_OBSERVATION_TITLE
    : 'Mental State Examination';
  // Must stay index-aligned with stepKeys: the sidebar zips the two lists.
  const navSections = useMemo(() => [
    ...sections.map(([, section]) => ({
      sectionTitle: section.sectionTitle,
      status: section.status,
    })),
    ...(hideMseStep ? [] : [{
      sectionTitle: mseStepTitle,
      status: visitedSteps[MSE_SECTION_ID] ? 'completed' : undefined,
    }]),
    {
      sectionTitle: 'Risk Assessment',
      status: visitedSteps[RISK_SECTION_ID] ? 'completed' : undefined,
    },
  ], [sections, visitedSteps, mseStepTitle, hideMseStep]);


  // ── AI paragraph generation on section exit (ASMT-19) ───────────────
  // Mirrors AdultAutismHistory: hasChanges tracks edits since entering the
  // current section; managed navigation (goToSection, handleGoToSSI) fires
  // generation for the section being left, and the exit-regeneration hook is
  // the safety net for unmanaged exits (breadcrumb, browser back, tab close).
  const [hasChanges, setHasChanges] = useState(false);

  const sectionTitleForKey = useCallback((key) => {
    if (key === MSE_SECTION_ID) return 'Mental State Examination';
    if (key === RISK_SECTION_ID) return 'Risk Assessment';
    return ciData.sections?.[key]?.sectionTitle || '';
  }, [ciData.sections]);

  const activeSectionTitle = sectionTitleForKey(activeSectionKey);

  // Determines whether the current section has enough data for the backend
  // extractor to produce a valid LLM input. Reads from the LOCAL optimistic
  // copies (mseData, riskAssessmentData) rather than the Firestore snapshot
  // (ciStage), because the debounced write may not have landed by the time
  // the clinician navigates away. Every other shouldRegenerate consumer in
  // the codebase derives from local state; this aligns the CI page with
  // that established pattern.
  //
  // Prefill sections (Presenting Complaint, Education History, etc.) always
  // return true because they are seeded at patient creation and the backend
  // extractor handles them without a missing-field guard.
  const canGenerateForSection = useCallback((sectionTitle) => {
    if (sectionTitle === 'Mental State Examination') {
      return Object.keys(mseData).length > 0;
    }
    if (sectionTitle === 'Risk Assessment') {
      // Both risk panels feed the same Risk Assessment paragraph; the
      // backend reads whichever field this patient's panel writes
      // (structuredRiskAssessment or riskAssessment) and picks the matching
      // prompt. Either way the Cloud Function rejects an empty block, so
      // the gate is "has the clinician recorded anything yet".
      if (useStructuredRisk) return hasAnyStructuredRiskInput(structuredRiskData);
      return hasAnyRiskAssessmentInput(riskAssessmentData);
    }
    return true;
  }, [mseData, riskAssessmentData, structuredRiskData, useStructuredRisk]);

  const { markRegenHandled, resetRegenHandled } = useModularSectionExitRegeneration({
    patientId,
    stageKey: STAGE_KEY,
    sectionTitle: activeSectionTitle,
    hasChanges,
    canEdit,
    reportContent,
    conditions: [],
    // Guard for unmanaged exits (breadcrumb, browser back, tab close): an
    // empty MSE/Risk step must not fire a generation the Cloud Function
    // will reject. Uses the local optimistic copy so the guard is correct
    // even when the debounced Firestore write has not landed yet.
    shouldRegenerate: canGenerateForSection(activeSectionTitle),
    assessmentModule: 'cypAdhd',
  });

  /**
   * Trigger LLM paragraph generation for one CI section. Skips read-only
   * views, Patient Information (no AI paragraph — parity with the backend
   * SECTION_PARAGRAPHS and the exit hook's guard), and sections that are
   * already generated with no changes.
   */
  const triggerCiGeneration = useCallback((sectionTitle) => {
    if (!canEdit || !sectionTitle) return;
    if (sectionTitle === 'Patient Information') return;
    // MSE and Risk have nothing on the stage doc until the clinician records
    // something; requesting their paragraph before then makes the Cloud
    // Function fail on the missing field. Skipping leaves the section out of
    // stageSectionsGenerated, so it still generates on a later exit once
    // data exists. Uses the local optimistic state (via canGenerateForSection)
    // so the guard is accurate even before the debounced Firestore write lands.
    if (!canGenerateForSection(sectionTitle)) return;

    const stageReport = reportContent?.[STAGE_KEY];
    const generated = stageReport?.stageSectionsGenerated || [];
    const isGenerated = generated.includes(sectionTitle);

    if (hasChanges || !isGenerated) {
      const dataReady = flushStageWrites(STAGE_KEY);
      TriggerGenerateDocumentSection(
        patientId, 'CYP ADHD Clinical Interview', sectionTitle, [], dataReady,
      );
    }
  }, [canEdit, patientId, reportContent, hasChanges, flushStageWrites, canGenerateForSection]);

  /** Promote a never-started stage to inProgress alongside any update. */
  const withStatusPromotion = useCallback((updates) => {
    if (!ciStage?.status || ciStage.status === 'notStarted') {
      return { ...updates, status: 'inProgress' };
    }
    return updates;
  }, [ciStage?.status]);

  const persistNotes = useCallback((sectionKey, subKey, notes) => {
    if (!canEdit) return;
    const path = `data.sections.${sectionKey}.subsections.${subKey}.clinicianNotes`;
    setLocalNotes((prev) => ({ ...prev, [path]: notes }));
    setHasChanges(true);
    updateAssessmentStage(STAGE_KEY, withStatusPromotion({ [path]: notes }));
  }, [canEdit, updateAssessmentStage, withStatusPromotion]);

  // ── Moving a note between subsection boxes ────────────────────────────
  // Notes are addressed by their stage dot-path, which is already unique per
  // box; StructuredNotesEditor needs that id to tell a same-box reorder from
  // a move out of the box (with both ids undefined every cross-box drag
  // silently did nothing). The catalogue spans all sections, so the Move
  // picker relocates a note written under the wrong heading without the
  // clinician retyping it.
  const notesPathFor = useCallback(
    (sectionKey, subKey) => `data.sections.${sectionKey}.subsections.${subKey}.clinicianNotes`,
    [],
  );

  const noteMoveTargets = useMemo(() => {
    const targets = [];
    sortedEntries(ciData?.sections || {}).forEach(([sectionKey, section]) => {
      // Patient Information has no notes box, so it is not a Move destination.
      if (section?.sectionTitle === 'Patient Information') return;
      // Same filter and numbering as the rendered panels, so a note can
      // never be moved into a panel the clinician cannot see.
      visibleCiSubsections(section, showPeriodsContent).forEach(([subKey, subsection], index) => {
        const rawTitle = subsection?.subsectionDisplayTitle || subsection?.subsectionTitle;
        targets.push({
          id: notesPathFor(sectionKey, subKey),
          label: rawTitle
            ? numberedPanelTitle(rawTitle, index)
            : `Subsection ${index + 1}`,
          group: section?.sectionTitle || `Section ${sectionKey}`,
        });
      });
    });
    return targets;
  }, [ciData?.sections, notesPathFor, showPeriodsContent]);

  const handleMoveNote = useCallback((sourcePath, noteId, targetPath, insertAt, notePatch) => {
    if (!canEdit) return;
    if (!sourcePath || !targetPath || sourcePath === targetPath) return;

    const readBox = (path) => {
      if (localNotes[path] !== undefined) return localNotes[path];
      const match = /^data\.sections\.(.+)\.subsections\.(.+)\.clinicianNotes$/.exec(path);
      if (!match) return [];
      const [, sectionKey, subKey] = match;
      return normaliseNotesValue(
        ciData?.sections?.[sectionKey]?.subsections?.[subKey]?.clinicianNotes,
      );
    };

    const result = moveNoteBetweenArrays(
      readBox(sourcePath), readBox(targetPath), noteId, insertAt, notePatch,
    );
    if (!result) return;

    // One write for both boxes — a half-applied move duplicates or loses the note.
    setLocalNotes((prev) => ({
      ...prev, [sourcePath]: result.source, [targetPath]: result.target,
    }));
    setHasChanges(true);
    updateAssessmentStage(STAGE_KEY, withStatusPromotion({
      [sourcePath]: result.source,
      [targetPath]: result.target,
    }));
  }, [canEdit, localNotes, ciData?.sections, updateAssessmentStage, withStatusPromotion]);

  const goToSection = useCallback((index, { confirmed = false } = {}) => {
    const targetKey = stepKeys[index];
    if (!targetKey) return;

    // Moving on to a later stage while the parent/carer ID check is still
    // unticked pauses here. Back, and re-selecting the stage already open,
    // do not ask. A read-only viewer cannot record the check, so they are
    // not stopped. `confirmed` is the reminder's own "continue" — without
    // it, agreeing in the dialog would open the dialog again.
    const movingForward = index > activeIndex;
    if (
      !confirmed
      && movingForward
      && canEdit
      && !parentCarerIdVerification?.verified
    ) {
      idCheckPromptIndexRef.current = index;
      setIdCheckPromptIndex(index);
      return;
    }
    if (idCheckPromptIndexRef.current != null) {
      idCheckPromptIndexRef.current = null;
      setIdCheckPromptIndex(null);
    }

    // Mark regeneration handled so the unmount safety net does not fire a
    // duplicate, generate for the section being left, then re-arm the hook
    // for the next section (mirrors AdultAutismHistory's goToSection).
    //
    // Re-selecting the CURRENT section is a no-op for generation, but the
    // dirty flag must survive it. Clearing hasChanges unconditionally meant
    // clicking the already-active sidebar row discarded the edit's claim on a
    // regeneration: nothing generated then, and the exit hook later saw an
    // already-generated section with no changes and skipped it, so the note
    // was saved to Firestore but never reached the report paragraph.
    markRegenHandled();
    if (targetKey !== activeSectionKey) {
      triggerCiGeneration(sectionTitleForKey(activeSectionKey));
      setHasChanges(false);

      // The section being LEFT goes green in the sidebar — the adult CI's
      // visited semantics (Sai, 28 Aug: the nav must show where you have
      // been). Data-model sections carry status in the stage data; MSE and
      // Risk are page-level steps, so their visits are stamped alongside.
      if (canEdit && activeSectionKey) {
        // Remember the section being left even when the stage is already
        // complete (the write below is skipped in that case). The finish
        // check reads this set because context has not caught up yet.
        visitedThisSession.current.add(activeSectionKey);
      }
      if (canEdit && ciStage?.status !== 'completed') {
        if (activeSectionKey === MSE_SECTION_ID || activeSectionKey === RISK_SECTION_ID) {
          updateAssessmentStage(STAGE_KEY, withStatusPromotion({
            [`data.visitedSteps.${activeSectionKey}`]: true,
          }));
        } else if (activeSectionKey) {
          updateAssessmentStage(STAGE_KEY, withStatusPromotion({
            [`data.sections.${activeSectionKey}.status`]: 'completed',
          }));
        }
      }
    }
    resetRegenHandled();

    setActiveSectionKey(targetKey);
    // Progress = how many sections have actually been VISITED, counted from
    // the stamps written just above, plus the one being opened now.
    //
    // It used to be (targetIndex + 1) / stepCount, i.e. purely how far the
    // clinician had navigated in one direction. Anyone who worked through the
    // sections out of order — or who used the sidebar to jump back and then
    // forward — saw a percentage far below what they had actually recorded,
    // which reads as lost work (Eve, 1 Sept: "it's not saving the progress").
    // Counting stamps is order-independent and matches the green sidebar
    // ticks the clinician can see.
    const visitedKeys = new Set();
    stepKeys.forEach((key) => {
      if (key === MSE_SECTION_ID || key === RISK_SECTION_ID) {
        if (ciStage?.data?.visitedSteps?.[key]) visitedKeys.add(key);
      } else if (ciStage?.data?.sections?.[key]?.status === 'completed') {
        visitedKeys.add(key);
      }
    });
    // The section being left was stamped above; the one being opened counts
    // as reached. Neither is in the context snapshot yet.
    if (activeSectionKey) visitedKeys.add(activeSectionKey);
    visitedKeys.add(targetKey);

    const reached = Math.min(1, visitedKeys.size / stepKeys.length);
    const existing = typeof ciStage?.progress === 'number' ? ciStage.progress : 0;
    if (canEdit && reached > existing && ciStage?.status !== 'completed') {
      updateAssessmentStage(STAGE_KEY, withStatusPromotion({ progress: reached }));
    }

    const targetSlug = slugForSectionKey(targetKey, sections);
    slugWeSet.current = targetSlug;
    navigate(`/patient/${patientId}/proforma/cyp-adhd/clinical-interview/${targetSlug}`);
  }, [stepKeys, activeSectionKey, activeIndex, ciStage?.progress, ciStage?.status,
    ciStage?.data?.sections, ciStage?.data?.visitedSteps, canEdit,
    parentCarerIdVerification?.verified,
    updateAssessmentStage, withStatusPromotion, markRegenHandled,
    resetRegenHandled, triggerCiGeneration, sectionTitleForKey,
    sections, navigate, patientId]);

  // A hotlink or the Back button names a section the click handler did not.
  // A stage-document refresh must not be read as a navigation: the address
  // bar can still show the previous slug for a moment after Next is clicked.
  const lastSeenSlug = useRef(sectionSlug);
  useEffect(() => {
    const slugChanged = lastSeenSlug.current !== sectionSlug;
    lastSeenSlug.current = sectionSlug;
    if (!sectionSlug || !ciStage || stepKeys.length === 0) return;
    if (!slugChanged && slugWeSet.current) return;
    if (sectionSlug === slugWeSet.current) return;
    const match = stepKeys.find((key) => slugForSectionKey(key, sections) === sectionSlug);
    if (!match) {
      // The slug names a step this patient does not have (a stale link to
      // the Mental State Examination on a TOC record, say). The state
      // initialiser may already have picked that step up from the slug, so
      // move the page to the first step as well as the address bar —
      // otherwise the hidden step would still render under the new URL.
      const fallback = slugForSectionKey(stepKeys[0], sections);
      slugWeSet.current = fallback;
      setActiveSectionKey(stepKeys[0]);
      navigate(
        `/patient/${patientId}/proforma/cyp-adhd/clinical-interview/${fallback}`,
        { replace: true },
      );
      return;
    }
    slugWeSet.current = sectionSlug;
    setActiveSectionKey(match);
  }, [sectionSlug, stepKeys, sections, navigate, patientId, ciStage]);

  // Deep-merge partial risk updates and persist the whole riskAssessment
  // object — the exact contract HistoryRiskAssessmentPanel expects
  // (mirrors handleRiskAssessmentUpdate in AdultAutismHistory).
  const handleRiskAssessmentUpdate = useCallback((partialUpdate) => {
    const nextData = deepMerge(riskAssessmentData, partialUpdate);
    setRiskAssessmentData(nextData);
    if (!canEdit) return;
    setHasChanges(true);
    updateAssessmentStage(STAGE_KEY, withStatusPromotion({ riskAssessment: nextData }));
  }, [riskAssessmentData, canEdit, updateAssessmentStage, withStatusPromotion]);

  // Structured risk (TOC): the panel hands back the complete next object and
  // it is persisted wholesale — replace, never deep-merge, because detail
  // selections are arrays and a merge would resurrect deselected options.
  const handleStructuredRiskChange = useCallback((nextData) => {
    setStructuredRiskData(nextData);
    if (!canEdit) return;
    setHasChanges(true);
    updateAssessmentStage(STAGE_KEY, withStatusPromotion({ structuredRiskAssessment: nextData }));
  }, [canEdit, updateAssessmentStage, withStatusPromotion]);

  // Medical screening (WLC): the panel hands back the complete next object
  // and it is persisted wholesale under medicalScreening, like structured
  // risk. The ONE deliberate difference from handleStructuredRiskChange:
  // this does NOT call setHasChanges(true). hasChanges is the "a report
  // input changed" flag that fires a section-exit regeneration of the
  // Medical History / Family History paragraph. These Yes/No decisions are
  // not an input to those paragraphs (they are never sent to the drafting
  // step), so marking the section dirty would re-draft text the clinician
  // may already have approved, and spend a generation, for no change in
  // the output.
  const handleMedicalScreeningChange = useCallback((nextData) => {
    setMedicalScreeningData(nextData);
    if (!canEdit) return;
    updateAssessmentStage(STAGE_KEY, withStatusPromotion({ medicalScreening: nextData }));
  }, [canEdit, updateAssessmentStage, withStatusPromotion]);

  // Parent/carer ID check. A tick is a signed record of who confirmed the
  // ID and when; clearing the box deletes that record rather than leaving
  // a "verified" stamp against an empty box. This is not an input to any
  // report paragraph (Patient Information is never drafted), so it does not
  // mark the section as changed for regeneration — the same reason the
  // medical-screening toggles leave hasChanges alone.
  const handleParentCarerIdVerificationChange = useCallback((checked) => {
    if (!canEdit) return;
    const next = checked
      ? {
          verified: true,
          verifiedAt: new Date().toISOString(),
          verifiedBy: user?.uid || null,
          verifiedByName: user?.displayName || '',
        }
      : null;
    setParentCarerIdVerification(next);
    updateAssessmentStage(STAGE_KEY, withStatusPromotion({
      parentCarerIdVerification: next,
    }));
  }, [canEdit, user?.uid, user?.displayName, updateAssessmentStage, withStatusPromotion]);

  // MSE: persisted under the ci stage doc's "mse" field. Merge semantics are
  // explicit rather than deepMerge: a payload carrying isAppropriateSelection
  // is a Yes/No toggle and REPLACES the category wholesale (clearing the
  // other side's selections and any stale subcategory data — deepMerge would
  // silently keep them); anything else merges into the category, with
  // subcategory selects merged per option.
  // NOTE: mseData state and its Firestore sync effect are declared above
  // (after riskAssessmentData) so shouldRegenerate and triggerCiGeneration
  // can reference the live copy.

  const mergeMseCategory = (existing = {}, payload) => {
    // Scalar payloads (e.g. the Mood free-text description string) are
    // stored as-is, not merged.
    if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) {
      return payload;
    }
    if (Object.prototype.hasOwnProperty.call(payload, 'isAppropriateSelection')) {
      return { ...payload };
    }
    const next = { ...existing, ...payload };
    if (payload.subcategories) {
      next.subcategories = { ...(existing.subcategories || {}) };
      Object.entries(payload.subcategories).forEach(([optionName, subs]) => {
        next.subcategories[optionName] = {
          ...(next.subcategories[optionName] || {}),
          ...subs,
        };
      });
    }
    return next;
  };

  const handleMseUpdate = useCallback((partialUpdate) => {
    setMseData((prev) => {
      const nextData = { ...prev };
      Object.entries(partialUpdate).forEach(([subVar, categories]) => {
        // The Additional Notes card writes a TERMINAL VALUE at the top level
        // (mse.additionalNotes) rather than a { categoryName: patch } map:
        // originally a plain string, and since the smart-notes change an
        // ARRAY of note objects. Every other key is a category map.
        //
        // Both terminal shapes must short-circuit. Object.entries on a string
        // yields its characters, which turned "Bright and chatty." into
        // { 0: {...} } and rendered "[object Object]"; an array hit the same
        // path via typeof [] === 'object' and became { 0: note }, which
        // normaliseNotesValue reads as empty — so a typed note vanished with
        // no bullet and no error (Eve, 1 Sept). Arrays are values here, never
        // category maps.
        if (typeof categories !== 'object' || categories === null || Array.isArray(categories)) {
          nextData[subVar] = categories;
          return;
        }
        nextData[subVar] = { ...(nextData[subVar] || {}) };
        Object.entries(categories).forEach(([categoryName, payload]) => {
          nextData[subVar][categoryName] = mergeMseCategory(
            nextData[subVar][categoryName], payload,
          );
        });
      });
      if (canEdit) {
        setHasChanges(true);
        updateAssessmentStage(STAGE_KEY, withStatusPromotion({ mse: nextData }));
      }
      return nextData;
    });
  }, [canEdit, updateAssessmentStage, withStatusPromotion]);

  // ── Phase jump: Clinical Interview -> Semi-Structured Interview ──────────
  // The footer offers "Go to Semi-Structured Interview" as a small link
  // beneath Next on every earlier section, and as the forward action on the
  // last section. There is no confirmation dialog: leaving for the SSI is
  // the action. In edit mode the jump is only offered to a clinician who is
  // ALSO assigned to the SSI (canStartAppointment), so a CI-only clinician
  // is never led into an SSI they cannot write. In preview the gate is
  // bypassed so a previewer can walk CI -> SSI read-only.
  // The SSI's first phase. observationInSsi opens on the Clinical
  // Observation, because that variant has no MSE here and the child is seen
  // first. noObservationInSsi (WLC and TOC) opens on the symptom walk.
  // Landing an observationInSsi clinician on the symptoms would skip the
  // phase that replaced their MSE.
  const ssiFirstSlug = wf.cypHasObservationInSsi ? 'observation' : 'symptoms';
  const ssiPath = `/patient/${patientId}/proforma/cyp-adhd/semi-structured-interview/${ssiFirstSlug}`;
  const canProceedToSSI = isPreviewMode
    || (canEdit && canStartAppointment(header, user?.uid, ssiPath));

  const handleGoToSSI = useCallback(async () => {
    // An unticked For Consideration box blocks the jump even if the control
    // was reached some other way (a disabled button can still receive a
    // programmatic click).
    if (riskNoticeUnacknowledged) return;
    markRegenHandled();
    if (canEdit) {
      triggerCiGeneration(sectionTitleForKey(activeSectionKey));

      // Adult ADHD's CI sidebar turns a section green once the clinician has
      // left it (section.status === 'completed'). Prefill sections have no
      // extra question-level gate: visiting and moving on is what "complete"
      // means. MSE and Risk are not data-model sections, so their green tick
      // is the visit stamp instead. The section being left counts, because
      // this navigation is the visit that writes that stamp.
      const sectionAlreadyRecorded = (key) => {
        if (visitedThisSession.current.has(key)) return true;
        if (key === MSE_SECTION_ID || key === RISK_SECTION_ID) {
          return Boolean(ciData.visitedSteps?.[key]);
        }
        return ciData.sections?.[key]?.status === 'completed';
      };
      const everySectionRecorded = stepKeys.length > 0 && stepKeys.every(
        (key) => key === activeSectionKey || sectionAlreadyRecorded(key),
      );

      const updates = {};
      // A finished interview does not need its visit stamps rewritten. An
      // in-progress one does, so the sidebar tick survives the jump.
      if (ciStage?.status !== 'completed' && activeSectionKey) {
        if (activeSectionKey === MSE_SECTION_ID || activeSectionKey === RISK_SECTION_ID) {
          updates[`data.visitedSteps.${activeSectionKey}`] = true;
        } else {
          updates[`data.sections.${activeSectionKey}.status`] = 'completed';
        }
      }

      // The whole module is complete only when every section qualifies.
      // Jumping ahead from an early section still leaves, but the Patient
      // Overview card stays in progress until the rest have been visited.
      // firstCompletedAt is write-once (the overview uses it as the estimated
      // appointment date); lastCompletedAt refreshes on every finish.
      let didWriteFirstCompleted = false;
      if (everySectionRecorded) {
        const now = new Date().toISOString();
        updates.status = 'completed';
        updates.progress = 1;
        updates.lastCompletedAt = now;
        updates.lastCompletedBy = user?.uid || null;
        updates.lastCompletedByName = user?.displayName || null;
        if (!ciStage?.firstCompletedAt) {
          didWriteFirstCompleted = true;
          updates.firstCompletedAt = now;
          updates.firstCompletedBy = user?.uid || null;
          updates.firstCompletedByName = user?.displayName || null;
        }
      }

      if (Object.keys(updates).length > 0) {
        const payload = updates.status === 'completed'
          ? updates
          : withStatusPromotion(updates);
        updateAssessmentStage(STAGE_KEY, payload);
      }
      if (everySectionRecorded) {
        // Fire-and-forget. A failure here must not stop the clinician
        // reaching the Semi-Structured Interview.
        recordInferredAppointmentDateOnCompletion({
          patientId,
          moduleKey: 'ci',
          didWriteFirstCompleted,
          enabled: Boolean(wf.isAppointmentDatesEnabled),
        });
      }
      // Let the writes land before the page unmounts (a previewer has none).
      await flushStageWrites(STAGE_KEY);
    }
    navigate(ssiPath);
  }, [riskNoticeUnacknowledged, canEdit, markRegenHandled, triggerCiGeneration,
    sectionTitleForKey, activeSectionKey, ciData.visitedSteps, ciData.sections,
    stepKeys, ciStage?.status, ciStage?.firstCompletedAt, user, patientId,
    wf.isAppointmentDatesEnabled, withStatusPromotion, updateAssessmentStage,
    flushStageWrites, navigate, ssiPath]);

  // Scroll the content column back to the top on every section change, as the
  // autism History does. Guarded because jsdom has no Element.scrollTo.
  useEffect(() => {
    if (contentRef.current?.scrollTo) {
      contentRef.current.scrollTo({ top: 0 });
    }
  }, [activeSectionKey]);

  const activeStepTitle = navSections[activeIndex]?.sectionTitle || '';
  const prevStepTitle = activeIndex > 0
    ? navSections[activeIndex - 1]?.sectionTitle : 'Patient Overview';
  const nextStepTitle = navSections[activeIndex + 1]?.sectionTitle || '';

  // Three levels, matching the adult CI: the current section is part of the
  // trail, so a clinician can see where they are without reading the sidebar.
  const breadcrumbTuples = [
    ['Patient Overview', `/patient/${patientId}/cyp-adhd`],
    ['Clinical Interview', null],
    ...(activeStepTitle ? [[activeStepTitle, null]] : []),
  ];

  // An error state, not an endless spinner: a permission-denied or network
  // failure used to render "Loading…" forever with nothing to act on.
  if (error) {
    return (
      <Layout activePage="dashboard">
        <Breadcrumb tupleList={breadcrumbTuples} />
        <div className="p-8 max-w-2xl text-left">
          <h1 className="text-lg font-semibold text-gray-900 mb-2">
            This Clinical Interview could not be loaded
          </h1>
          <p className="text-sm text-gray-700 mb-4">
            Something went wrong fetching this assessment. Your work has not
            been lost. Please check your connection and try again, or go back to
            the patient overview.
          </p>
          <button
            type="button"
            onClick={() => navigate(`/patient/${patientId}/cyp-adhd`)}
            className="text-sm font-medium text-violet-700 border border-violet-300 rounded-md px-4 py-2 hover:bg-violet-50"
          >
            Back to patient overview
          </button>
        </div>
      </Layout>
    );
  }

  if (!ciStage) {
    return (
      <Layout activePage="dashboard">
        <Breadcrumb tupleList={breadcrumbTuples} />
        <div className="p-8 text-sm text-gray-500">Loading the Clinical Interview…</div>
      </Layout>
    );
  }

  const isLastSection = activeIndex === stepKeys.length - 1;

  // The greyed forward button stays clickable on purpose. A truly disabled
  // button shows no hover note in most browsers and cannot be used to bring
  // the warning back into view. This scrolls the For Consideration banner
  // to the top of the interview and puts the keyboard on its checkbox.
  const scrollToRiskNotice = () => {
    const notice = document.getElementById(CYP_RISK_NOTICE_ID);
    const reduceMotion = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior = reduceMotion ? 'auto' : 'smooth';
    if (notice?.scrollIntoView) {
      notice.scrollIntoView({ behavior, block: 'start' });
      notice.querySelector('input[type="checkbox"]')?.focus({ preventScroll: true });
      return;
    }
    contentRef.current?.scrollTo?.({ top: 0, behavior });
  };

  /**
   * Footer forward action on the LAST step:
   *   - the assigned clinician gets "Go to Semi-Structured Interview".
   *     Clicking it leaves for the SSI and, when every section has been
   *     visited, marks this module complete. While a Yes risk is
   *     unacknowledged the same button is greyed: hover explains the
   *     checkbox, and a click jumps to it;
   *   - a read-only viewer who may proceed (preview, or assigned to the SSI)
   *     gets the same label as plain navigation;
   *   - anyone else gets no forward CTA — offering a finish action to
   *     someone who cannot finish would present an action that only navigates.
   * Non-final steps return undefined so the footer renders its default Next.
   */
  const SSI_FORWARD_LABEL = 'Go to Semi-Structured Interview';
  const footerForwardActions = (() => {
    if (!isLastSection) return undefined;
    if (canEdit) {
      return (
        <span className="relative inline-flex group">
          <button
            type="button"
            onClick={riskNoticeUnacknowledged ? scrollToRiskNotice : handleGoToSSI}
            aria-disabled={riskNoticeUnacknowledged || undefined}
            aria-describedby={riskNoticeUnacknowledged ? 'cyp-risk-forward-tooltip' : undefined}
            className={riskNoticeUnacknowledged
              ? 'flex items-center py-2 px-5 rounded-md shadow-sm font-semibold text-sm bg-gray-300 text-gray-500 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2'
              : `${PRIMARY_FORWARD_BTN} px-5 whitespace-nowrap`}
          >
            <span className="font-semibold text-sm">{SSI_FORWARD_LABEL}</span>
            <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />
          </button>
          {riskNoticeUnacknowledged && (
            <span
              id="cyp-risk-forward-tooltip"
              role="tooltip"
              className="pointer-events-none absolute bottom-full right-0 z-20 mb-2 w-72 rounded-lg bg-slate-900 p-3 text-left text-sm font-semibold text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100"
            >
              {riskForwardHint}
            </span>
          )}
        </span>
      );
    }
    if (!canProceedToSSI) return null;
    return (
      <button
        type="button"
        onClick={handleGoToSSI}
        className={`${PRIMARY_FORWARD_BTN} px-5 whitespace-nowrap`}
      >
        <span className="font-semibold text-sm">{SSI_FORWARD_LABEL}</span>
        <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />
      </button>
    );
  })();

  // Page-level heading — History's 3xl unnumbered section title. The
  // contract-type badge sits opposite it on Patient Information only (it is
  // patient-level metadata, not something to repeat on every section).
  const sectionHeading = (title, showContractTypeBadge = false) => (
    <div className="flex justify-between items-center gap-3">
      <h2 className="text-3xl font-bold text-gray-900 text-left">{title}</h2>
      {showContractTypeBadge && (
        <PatientContractTypeBadge
          label="Contract"
          organisation={claims?.organisation}
          assessmentType={header?.customFields?.assessmentType}
          subContractType={header?.customFields?.subContractType}
        />
      )}
    </div>
  );

  // A previewer sees a neutral empty state; a non-assigned clinician is told
  // why the box is closed (History's readOnlyNotesPlaceholder).
  const readOnlyNotesPlaceholder = isPreviewMode
    ? 'No notes recorded for this subsection'
    : 'Notes can only be added by the assigned clinician';

  return (
    <Layout activePage="dashboard" scrollable={false}>
      <div className="flex flex-col h-full min-h-0">
      <div className="flex-shrink-0">
        <Breadcrumb tupleList={breadcrumbTuples} />
      </div>

      {/* Main layout — sidebar full height, content + footer on the right */}
      <div className="flex flex-1 min-h-0">
        {/* Left sidebar — the shared autism ModularSideNavbar. Each step's
            badge is its 1-based position; a step the clinician has moved on
            from (stamped completed / visited) shows the emerald tick. */}
        <ModularSideNavbar
          items={navSections.map((step, idx) => ({
            id: stepKeys[idx],
            badge: idx + 1,
            label: step.sectionTitle,
            fullTitle: step.sectionTitle,
            status: step.status === 'completed' ? 'completed' : 'default',
            isUnlocked: true,
          }))}
          activeId={activeSectionKey}
          onSelect={(id) => {
            const idx = stepKeys.indexOf(id);
            if (idx >= 0) goToSection(idx);
          }}
        />

        {/* Right column — scrollable content + fixed footer */}
        <div className="flex-1 flex flex-col min-h-0">
          {isPreviewMode && <PreviewModeBanner />}

          <div ref={contentRef} className="flex-1 overflow-y-auto p-8">
            {isMseStep ? (
              // Heading above the panel stack with the same 24px rhythm as a
              // History section (the panel lays out its own cards in space-y-6).
              <div className="w-full text-left space-y-6">
                {sectionHeading(mseStepTitle)}
                {useClinicalObservationMse ? (
                  <CypAdhdMsePanel
                    mseData={mseData}
                    onMseUpdate={handleMseUpdate}
                    canEdit={canEdit}
                    subsections={CYP_CLINICAL_OBSERVATION_SUBSECTIONS}
                    intro={CYP_CLINICAL_OBSERVATION_INTRO}
                    decisionLabel={CYP_CLINICAL_OBSERVATION_DECISION_LABEL}
                    decisionTooltip={CYP_CLINICAL_OBSERVATION_DECISION_TOOLTIP}
                    title={CYP_CLINICAL_OBSERVATION_TITLE}
                  />
                ) : (
                  <CypAdhdMsePanel
                    mseData={mseData}
                    onMseUpdate={handleMseUpdate}
                    canEdit={canEdit}
                  />
                )}
              </div>
            ) : isRiskStep ? (
              <div className="w-full text-left">
                {useStructuredRisk ? (
                  <div className="space-y-6">
                    {sectionHeading('Risk Assessment')}
                    <CypAdhdStructuredRiskPanel
                      riskData={structuredRiskData}
                      onRiskDataChange={handleStructuredRiskChange}
                      canEdit={canEdit}
                    />
                  </div>
                ) : (
                  // HistoryRiskAssessmentPanel renders its own 3xl "Risk
                  // Assessment" heading (it is the autism History's risk page
                  // verbatim), so the page adds none above it.
                  <HistoryRiskAssessmentPanel
                    riskAssessmentData={riskAssessmentData}
                    onRiskAssessmentUpdate={handleRiskAssessmentUpdate}
                    canEdit={canEdit}
                    // The panel derives its Tag action from this prop, so
                    // withholding the catalogue is what hides tagging here —
                    // there is no enableCriteriaTagging of its own to gate.
                    noteTagOptions={CYP_NOTE_TAGGING_ENABLED ? CYP_NOTE_TAG_OPTIONS : undefined}
                  />
                )}
              </div>
            ) : activeSection ? (
              // History's SectionPanel: heading and panels stacked with space-y-6.
              <div className="w-full text-left space-y-6">
                {sectionHeading(
                  activeSection.sectionTitle,
                  activeSection.sectionTitle === 'Patient Information',
                )}

                {visibleCiSubsections(activeSection, showPeriodsContent).map(([subKey, subsection], panelIndex) => {
                  const rows = sortedEntries(subsection.subsectionQuestions);
                  // One school column for the whole panel when ANY question in
                  // it was asked of the school, so the two columns line up down
                  // the panel exactly as the autism informant column does.
                  const panelHasSchoolColumn = rows.some(
                    ([, node]) => Boolean(node.hasSchoolColumn),
                  );
                  const subsectionTitle = numberedPanelTitle(
                    resolvePatientFirstNameInText(
                      subsection.subsectionDisplayTitle || subsection.subsectionTitle,
                      header?.firstName,
                    ),
                    panelIndex,
                  );
                  return (
                    <div key={subKey} className={CI_PANEL_CLASS}>
                      {/* Panel header: title in the label column, icon column
                          headers above the answers (History's layout). */}
                      <div className="flex items-center mb-2">
                        <h4 className="font-bold text-lg w-56 flex-shrink-0">
                          {subsectionTitle}
                        </h4>
                        <SubsectionColumnHeaders
                          hasSchoolColumn={panelHasSchoolColumn}
                          schoolSource={ciData.schoolSource}
                          plainLabels={activeSection.sectionTitle === 'Patient Information'}
                        />
                      </div>

                      <div className="border-t border-gray-200 pt-4">
                        {/* Child details panel: prepend a static Name row from
                            the patient header (not a form question). */}
                        {subsection.subsectionVarName === 'childDetails' && (
                          <StaticChildNameRow header={header} />
                        )}
                        {rows.map(([qKey, node]) => (
                          <QuestionRow
                            key={qKey}
                            node={node}
                            panelHasSchoolColumn={panelHasSchoolColumn}
                            patientFirstName={header?.firstName}
                            guardianFormInstances={informantForms.guardian}
                            schoolFormInstances={informantForms.school}
                          />
                        ))}
                      </div>

                      {/* Parent/carer panel only (stable key informantDetails,
                          the second card on Patient Information). The ID
                          check sits where a notes box would, under the
                          answers. Keyed on the variable name rather than
                          "the second panel" so a reordered card cannot
                          move the check onto the child's details. */}
                      {subsection.subsectionVarName === 'informantDetails' && (
                        <ParentCarerIdVerification
                          verification={parentCarerIdVerification}
                          canEdit={canEdit}
                          onChange={handleParentCarerIdVerificationChange}
                          divided
                          inputId="parent-carer-id-check"
                        />
                      )}

                      {/* Clinician notes under a divider at the foot of the
                          panel, with no heading of their own — the editor's
                          placeholder names the subsection instead. Patient
                          Information is the exception: nothing drafts a
                          paragraph from this stage, so a notes box here would
                          store text that never reaches the report. */}
                      {activeSection.sectionTitle !== 'Patient Information' && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <StructuredNotesEditor
                            notes={
                              localNotes[`data.sections.${activeSectionKey}.subsections.${subKey}.clinicianNotes`]
                              ?? normaliseNotesValue(subsection.clinicianNotes)
                            }
                            onChange={(notes) => persistNotes(activeSectionKey, subKey, notes)}
                            domainId={notesPathFor(activeSectionKey, subKey)}
                            onMoveNoteIn={handleMoveNote}
                            moveTargets={noteMoveTargets}
                            currentMoveId={notesPathFor(activeSectionKey, subKey)}
                            onMoveNoteTo={handleMoveNote}
                            placeholder={`Type any notes about ${subsectionTitle}…`}
                            readOnlyPlaceholder={readOnlyNotesPlaceholder}
                            enableCriteriaTagging={CYP_NOTE_TAGGING_ENABLED}
                            tagOptions={CYP_NOTE_TAG_OPTIONS}
                            readOnly={!canEdit}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Treatment contraindication screening (WLC only): one
                    extra panel at the foot of Medical History and of Family
                    History, keyed on the data-model sectionVarName so a
                    renamed display title cannot move it. Numbered as the
                    section's next panel so it reads as part of the step. */}
                {(() => {
                  const screeningGroup = showMedicalScreening
                    ? medicalScreeningGroupForSection(activeSection.sectionVarName)
                    : null;
                  if (!screeningGroup) return null;
                  // Count the panels actually shown so the numbering continues
                  // from the last visible one.
                  const subsectionCount = visibleCiSubsections(activeSection, showPeriodsContent).length;
                  return (
                    <CypAdhdMedicalScreeningPanel
                      group={screeningGroup}
                      data={medicalScreeningData}
                      onChange={handleMedicalScreeningChange}
                      canEdit={canEdit}
                      title={numberedPanelTitle(screeningGroup.title, subsectionCount)}
                    />
                  );
                })()}
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                This Clinical Interview has no sections yet. If the patient was created
                before the CI structure shipped, ask an administrator to recreate them.
              </p>
            )}
          </div>

          {/* Fixed footer — History's ModularStepFooter. Back is named by the
              previous section; the counter is "n of N"; Next names the next
              section; on the last section the forward slot is replaced by the
              Complete / Go to SSI action above. The phase skip link sits under
              the CTA on every non-final section when the viewer may proceed.
              The last section uses the same leave action: no confirmation
              dialog. The module is marked complete only when every section
              has been visited. */}
          <ModularStepFooter
            showBack={activeIndex > 0}
            backLabel={prevStepTitle}
            onBack={() => goToSection(activeIndex - 1)}
            counterText={`${activeIndex + 1} of ${stepKeys.length}`}
            onNext={() => goToSection(activeIndex + 1)}
            nextLabel={`Next: ${nextStepTitle}`}
            rightActions={footerForwardActions}
            showNext={footerForwardActions === undefined && !isLastSection}
            phaseForwardLink={
              !isLastSection && canProceedToSSI
                ? {
                    label: 'Go to Semi-Structured Interview',
                    onClick: handleGoToSSI,
                    disabled: riskNoticeUnacknowledged,
                    title: riskAcknowledgementTitle,
                  }
                : undefined
            }
          />
        </div>
      </div>
      </div>
      {/* Forward moves pause here until the clinician records the parent/carer
          ID check or chooses to go on without it. Rendered beside the page,
          not inside a section, so it is the same dialog from Next and from
          the sidebar. */}
      <ParentCarerIdCheckDialog
        open={idCheckPromptIndex != null}
        verification={parentCarerIdVerification}
        canEdit={canEdit}
        onVerificationChange={handleParentCarerIdVerificationChange}
        onStay={() => {
          idCheckPromptIndexRef.current = null;
          setIdCheckPromptIndex(null);
        }}
        onContinue={() => {
          const index = idCheckPromptIndexRef.current;
          if (index == null) return;
          goToSection(index, { confirmed: true });
        }}
      />
    </Layout>
  );
}
